import { makeObservable, observable, runInAction, flow } from 'mobx';
import { ChatMessage, ChatBotConfig, ChatMessageType, ChatComponent, InformationOptions } from '../types';
import { ChatBotService } from '../services';
import { StreamHandler } from '../utils/streamHandler';
import { v4 as uuidv4 } from 'uuid';
import { ApiResponse, AuthenticationStore, GlobalStore } from '@roc/feature-app-core';
import { getOrigin } from '../utils/util';

const DEFAULT_CONFIG: ChatBotConfig = {
  welcomeMessage: 'Hello! How can I help you today?',
  placeholder: 'Type your message here...',
  botName: 'Roc Assistant',
};

/**
 * Manages chat state and orchestrates message flow
 */
export class ChatBotStore {
  globalStore: GlobalStore
  authenticationStore: AuthenticationStore;
  messages: ChatMessage[] = [];
  loading = false;
  config: ChatBotConfig;
  minimized = false;
  informationOptions: InformationOptions | null = null;

  private service: ChatBotService;
  private streamHandler: StreamHandler;
  private currentMessageId: string | null = null;

  constructor(globalStore: GlobalStore, authenticationStore: AuthenticationStore, config?: ChatBotConfig) {
    this.globalStore = globalStore;
    this.authenticationStore = authenticationStore;
    this.config = {
      ...DEFAULT_CONFIG,
      ...config,
      sessionId: config?.sessionId || uuidv4(),
    };

    this.service = new ChatBotService(authenticationStore);

    this.streamHandler = new StreamHandler(this.service);

    makeObservable(this, {
      config: observable,
      messages: observable,
      loading: observable,
      minimized: observable,
      sendMessageFeedback: flow,
      getMessagesHistory: flow,
      sendSessionFeedback: flow,
      informationOptions: observable,
      getInformationOptions: flow,
    });
  }

  private addMessage(message: ChatMessage): void {
    this.messages.push(message);
  }

  private updateMessage(id: string, text: string, isStreaming: boolean): void {
    const message = this.messages.find(m => m.id === id);
    if (message) {
      message.text = text;
      message.isStreaming = isStreaming;
    }
  }

  private addComponentToMessage(messageId: string, component: ChatComponent): void {
    const message = this.messages.find(m => m.id === messageId);
    if (message) {
      if (!message.components) {
        message.components = [];
      }
      message.components.push(component);
    }
  }

  private setLoading(loading: boolean): void {
    this.loading = loading;
  }

  /**
   * Initialize chat with welcome message
   */
  async initialize(): Promise<void> {
    if (this.messages.length > 0 || this.loading) {
      return;
    }

    // Skip initial load if configured
    if (this.config.skipInitialLoad) {
      return;
    }

    this.setLoading(true);
    this.createBotMessage(uuidv4());

    this.streamHandler.stream(
      { sessionId: this.config.sessionId!, message: 'hi', assistantMessageId: this.currentMessageId },
      {
        onToken: text => this.updateCurrentMessage(text, true),
        onContentUpdate: (messageId, text) => {
          runInAction(() => {
            this.updateMessage(messageId, text, true);
          });
        },
        onComponent: (messageId, component) => {
          runInAction(() => {
            this.addComponentToMessage(messageId, component);
          });
        },
        onComplete: () => this.handleStreamComplete(),
        onError: error => this.handleStreamError(error),
      }
    );
  }

  /**
   * Send a user message
   */
  async sendMessage(text: string): Promise<void> {
    if (!text.trim() || this.loading) {
      return;
    }
    const assistantMessageId = uuidv4();
    const userMessageId = uuidv4();
    this.addUserMessage(text, userMessageId);
    this.streamHandler.stop();
    this.setLoading(true);
    this.createBotMessage(assistantMessageId);

    this.streamHandler.stream(
      { sessionId: this.config.sessionId!, message: text, assistantMessageId, userMessageId },
      {
        onToken: text => this.updateCurrentMessage(text, true),
        onContentUpdate: (messageId, text) => {
          runInAction(() => {
            this.updateMessage(messageId, text, true);
          });
        },
        onComponent: (messageId, component) => {
          runInAction(() => {
            this.addComponentToMessage(messageId, component);
          });
        },
        onComplete: () => this.handleStreamComplete(),
        onError: error => this.handleStreamError(error),
        onNewMessage: () => this.createBotMessage(uuidv4()),
      }
    );
  }


  sendInactivityMessage() {
    this.addMessage({
      id: uuidv4(),
      text: 'The session will close due to inactivity in 5 minutes.',
      sender: ChatMessageType.ALERT,
      timestamp: new Date(),
    });
  }

  isInactivityMessageSent(): boolean {
    return this.messages.some(message => message.sender === ChatMessageType.ALERT);
  }

  isUserMessageSent(): boolean {
    return this.messages.some(message => message.sender === ChatMessageType.USER);
  }
  /**
   * Stop current streaming
   */
  stopStreaming(): void {
    this.streamHandler.stop();
    this.finalizeCurrentMessage();
    this.setLoading(false);
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.streamHandler.stop();
  }

  // Private helper methods

  private addUserMessage(text: string, userMessageId: string): void {
    this.removeLocalMessages();
    this.addMessage({
      id: userMessageId,
      text,
      sender: ChatMessageType.USER,
      timestamp: new Date(),
    });
  }

  private createBotMessage(botMessageId: string): void {
    this.currentMessageId = botMessageId;
    this.addMessage({
      id: this.currentMessageId,
      text: '',
      sender: ChatMessageType.BOT,
      timestamp: new Date(),
      isStreaming: true,
    });
  }

  private updateCurrentMessage(text: string, isStreaming: boolean): void {
    if (this.currentMessageId) {
      runInAction(() => {
        this.updateMessage(this.currentMessageId!, text, isStreaming);
      });
    }
  }

  private finalizeCurrentMessage(): void {
    if (this.currentMessageId) {
      const message = this.messages.find(m => m.id === this.currentMessageId);
      if (message) {
        runInAction(() => {
          message.isStreaming = false;
        });
      }
      this.currentMessageId = null;
    }
  }

  private handleStreamComplete(): void {
    runInAction(() => {
      this.finalizeCurrentMessage();
      this.setLoading(false);
    });
  }

  private handleStreamError(error: Error): void {
    console.error('Chat stream error:', error);
    runInAction(() => {
      if (this.currentMessageId) {
        const message = this.messages.find(m => m.id === this.currentMessageId);
        const errorText =
          message?.text || `Error: ${error.message || 'Please try again.'}`;
        this.updateMessage(this.currentMessageId, errorText, false);
      }
      this.setLoading(false);
      this.currentMessageId = null;
    });
  }

  reset(): void {
    this.stopStreaming();
    runInAction(() => {
      this.messages = [];
      this.config.sessionId = uuidv4();
    });
  }

  startNewChat(): void {
    this.reset();
    this.initialize();
  }

  removeLocalMessages(): void {
    this.messages = this.messages.filter(m =>
      m.sender !== ChatMessageType.ALERT &&
      m.sender !== ChatMessageType.SESSION_FEEDBACK
    );
  }

  setMinimized(minimized: boolean): void {
    this.minimized = minimized;
  }

  handleMaximize = () => {
    const isIframed = window.parent !== window;
    if (isIframed) {
      const message = { data: 'maximize-chat-bot' };
      window.parent.postMessage(message, getOrigin());
    } else {
      this.setMinimized(false);
    }
  }

  handleMinimize = () => {
    const isIframed = window.parent !== window;
    if (isIframed) {
      const message = { data: 'minimize-chat-bot' };
      window.parent.postMessage(message, getOrigin());
    } else {
      this.setMinimized(true);
    }
  }


  closeChatBotInParent = () => {
    const message = { data: 'close-chat-bot' };
    window.parent.postMessage(message, getOrigin());
  };

  *sendMessageFeedback(messageId: string, userMessage: string, reaction: number) {
    try {
      const response = yield this.service.sendMessageFeedback({ messageId, userMessage, reaction });
      if (response.data.status === 'OK') {
        console.log('Feedback sent successfully');
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Thanks for giving your feedback',
          options: {
            autoHideDuration: 1000,
          },
        });
      }
    } catch (error) {
      console.error('Error sending message feedback:', error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error sending feedback!',
      });
    }
  }

  *sendSessionFeedback(userMessage: string, rating: number) {
    try {
      const response = yield this.service.sendSessionFeedback({ sessionId: this.config.sessionId, userMessage, rating });
      if (response.data.status === 'OK') {
        console.log('Feedback sent successfully');
      }
    } catch (error) {
      console.error('Error sending message feedback:', error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error sending feedback!',
      });
    }
  }

  *getMessagesHistory() {
    try {
      const response = yield this.service.getMessagesHistory(this.config.sessionId!, 20, null);
      response.data.sort((a: any, b: any) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime());
      this.messages = response.data
        .filter((message: any) => message.content && message.messageId) // Filter out messages without content or messageId
        .map((message: any) => {
          if (message.component) {
            try {
              const data = JSON.parse(message.content);
              const type = message.componentType;
              return {
                id: message.messageId,
                text: '',
                sender: ChatMessageType.BOT,
                timestamp: new Date(message.createdDate),
                components: [{ type, data }],
              };
            } catch (e) {
              console.error('Error parsing component content:', e);
            }
          }
          return {
            id: message.messageId,
            text: message.content,
            sender: message.role === 'ASSISTANT' ? ChatMessageType.BOT : ChatMessageType.USER,
            timestamp: new Date(message.createdDate),
          };
        });
    } catch (error) {
      console.error('Error loading messages history:', error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error loading messages history.',
      });
    }
  }

  *getInformationOptions() {
    try {
      const response: ApiResponse = yield this.service.getInformationOptions();
      if (response.data.status === 'OK') {
        this.informationOptions = JSON.parse(response.data.data);
      } else {
        console.error('Error loading information options:', response.data?.error?.message || 'Unknown error');
        this.globalStore.notificationStore.showErrorNotification({
          message: 'Error loading information options.',
        });
      }
    } catch (error) {
      console.error('Error loading information options:', error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error loading information options.',
      });
    }
  }

}
